import { format } from 'date-fns'
import { groq } from 'next-sanity'
import ErrorPage from 'next/error'
import { useRouter } from 'next/router'
import React from 'react'
import { Card } from 'src/common/components/Card'
import { AppLayout } from 'src/common/components/Layout/AppLayout'
import { urlFor } from 'src/common/lib/sanity/sanity'
import { getClient } from 'src/common/lib/sanity/sanity.server'

const catQuery = groq`
  *[_type == "category" && slug.current == $slug][0] {
	title,
	"slug": slug.current,
	"relatedPosts": *[_type=='post' && references(^._id)]  | order(publishedAt desc){
		author->{
			name,
			"image": image.asset,
			"slug": slug.current,
		},
		publishedAt,
		title,
		"slug": slug.current,
		mainImage,
		categories[]->{
			title,
			"slug": slug.current,
		},
		"plaintext": pt::text(body),
	}
  }
`

const Category = ({ data }) => {
    const router = useRouter()

    if (!router.isFallback && !data.category?.slug) {
        return <ErrorPage statusCode={404} />
    }

    if (!data?.category) {
        return <ErrorPage statusCode={500} />
    }

    return (
        <AppLayout pageTitle={data.category.title}>
            <div className="h-36 flex items-center justify-center relative overflow-hidden">
                <h1 className="capitalize font-medium text-4xl">{data.category.title}</h1>
            </div>

            <div className="pl-2 grid grid-cols-1 justify-items-center md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7 gap-x-3 gap-y-9">
                {data.category.relatedPosts.map((item, idx) => (
                    <div className="block" key={idx}>
                        <Card
                            cardType="blog"
                            imageURL={urlFor(item.mainImage).width(225).height(150).url()}
                            title={item.title}
                            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                            href={`/blog/${item.slug}`}
                            publishedDate={format(new Date(item.publishedAt), 'MMM d')}
                            author={{
                                imageURL: urlFor(item.author.image)
                                    .width(38)
                                    .height(38)
                                    .url()
                            }}
                            category={
                                item.categories.length
                                    ? {
                                          color: '#8C80F8',
                                          name: item.categories[0]?.title,
                                          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                          href: `/blog/categories/${item.categories[0]?.slug}`
                                      }
                                    : null
                            }
                            previewText={item.plaintext.slice(0, 180)}
                        />
                    </div>
                ))}
            </div>
        </AppLayout>
    )
}

export async function getStaticProps({ params, preview = false }) {
    const category = await getClient(preview).fetch(catQuery, {
        slug: params.slug
    })

    return {
        props: {
            preview,
            data: { category }
        }
    }
}

export async function getStaticPaths() {
    const paths = await getClient(false).fetch(
        groq`*[_type == "category" && defined(slug.current)][].slug.current`
    )

    return {
        paths: paths.map((slug) => ({ params: { slug } })),
        fallback: true
    }
}

export default Category
